<template>
  <Modal framed :size="ModalSize.LG" :title="props.title">
    <template #activator>
      <Button
        :label="$t('send-modal.send')"
        :right-icon-name="IconName.SEND"
        :size="ButtonSize.SM"
        :variant="ButtonVariant.OUTLINED"
        :color="ButtonColor.TERTIARY"
      />
    </template>

    <template #body>
      <div class="flex flex-col items-start sm:flex-row flex-wrap gap-5">
        <div class="w-full sm:w-max flex items-center justify-center">
          <slot name="object-image" />
        </div>
        <div class="flex-1 text-left">
          <slot />
          <p class="text-2xl text-secondary font-bold">
            {{ props.objectToSend.title }}
          </p>
          <p class="text-on-surface-variant">
            {{ props.objectToSend.description }}
          </p>
        </div>
      </div>
      <div
        class="w-full px-3 py-2 border border-secondary rounded-full min-h-10 mt-8 flex items-center justify-center text-center text-secondary"
      >
        {{ currentUrl }}
      </div>
    </template>

    <template #footer="{ close }">
      <Button
        :label="$t('common.cancel')"
        :variant="ButtonVariant.TEXT"
        :color="ButtonColor.SECONDARY"
        @click="close"
      />

      <div class="flex gap-6">
        <div class="relative">
          <div class="hidden sm:block">
            <Button
              :label="$t('send-modal.copy-link')"
              :variant="ButtonVariant.OUTLINED"
              :color="ButtonColor.SECONDARY"
              @click="onCopyLink"
            />
          </div>
          <div class="sm:hidden">
            <IconButton
              :icon-name="IconName.COPY"
              :label="$t('send-modal.copy-link')"
              :variant="ButtonVariant.OUTLINED"
              :color="ButtonColor.SECONDARY"
              @click="onCopyLink"
            />
          </div>
          <p v-if="isLinkCopied" class="absolute text-xs text-success">
            {{ $t('send-modal.copy-link') }}
          </p>
        </div>
        <div class="hidden sm:block">
          <Button
            :label="$t('send-modal.email')"
            :right-icon-name="IconName.SEND"
            :to="getMailtoUrl()"
            @click="onEmailSent"
          />
        </div>
        <div class="sm:hidden">
          <IconButton
            :icon-name="IconName.SEND"
            :label="$t('send-modal.email')"
            :to="getMailtoUrl()"
            @click="onEmailSent"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconName,
  ModalSize,
  Modal,
} from '@exfluential/ui';
import { computed, ref } from '#imports';
import { ContentMetricType, createContentMetric } from '~/api/metrics';
import { copyToClipboard } from '~/utils/clipboard';

type Props = {
  hubSlug?: string;
  contentSlug?: string;
  objectToSend: Record<string, unknown>;
  title: string;
};
const props = defineProps<Props>();

const isLinkCopied = ref(false);

const currentUrl = computed(() => {
  if (globalThis) {
    return globalThis.location.href;
  }
  return '';
});

const onCopyLink = async () => {
  if (props.hubSlug && props.contentSlug) {
    createContentMetric(
      props.hubSlug,
      props.contentSlug,
      ContentMetricType.SENT_LINK_COPIED,
    );
  }

  const copied = await copyToClipboard(currentUrl.value);
  if (copied) {
    isLinkCopied.value = true;
    setTimeout(() => {
      isLinkCopied.value = false;
    }, 1000);
  }
};

const getMailtoUrl = () => {
  const encodedSubject = encodeURIComponent(
    `Link to ${props.objectToSend.title}`,
  );
  const encodedBody = encodeURIComponent(currentUrl.value);
  return `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
};

const onEmailSent = () => {
  if (props.hubSlug && props.contentSlug) {
    createContentMetric(
      props.hubSlug,
      props.contentSlug,
      ContentMetricType.SENT_EMAIL,
    );
  }
};
</script>
